import * as React from 'react';
import {Cell, Pie, PieChart, PieLabelRenderProps, ResponsiveContainer, Tooltip, TooltipProps} from 'recharts';
import {isEqual} from 'lodash';
import {groupNumber} from "../utils/number-utils";
import './utils/Statistics.css';
import {getColor} from "./utils/statistics-colors";
import {getPercentString, renderLegendDot, renderSmallText} from "./utils/statistics-utils";

const RADIAN = Math.PI / 180;

export interface ChartItem {
  name: string;
  value: number;
  avgValue?: number;
}

export type ChartData = ChartItem[] | undefined;

export interface Props {
  unit: string;
  data?: ChartData;
}

interface State {
  data?: ChartData;
  totalValue: number;
}

export class RentimeChart extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      data: props.data,
      totalValue: props.data ? this.sumChartItemValues(props.data) : 0,
    };
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const {data} = this.props;
    if (!isEqual(data, this.state.data)) {
      this.setState({
        data,
        totalValue: data ? this.sumChartItemValues(data) : 0
      });
    }
  }

  public render() {
    const {data} = this.state;
    const pieChartData = data ? data : undefined;
    const screenWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
    const radius = screenWidth < 576 ? "60%" : "80%";
    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            isAnimationActive={true}
            data={pieChartData}
            outerRadius={radius}
            dataKey="value"
            labelLine={this.renderLine}
            label={this.renderLabel}
          >
            {pieChartData &&
              pieChartData.map((entry, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColor(index)}
                  />
                );
              })
            }
          </Pie>
          <Tooltip content={this.renderTooltip}/>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  private sumChartItemValues = (data: ChartItem[]): number => {
    return data
      .map(item => item.value)
      .reduce((a: number, b: number) => {
        return a + b;
      }, 0);
  };

  private renderTooltip = (props: TooltipProps) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const {unit} = this.props;
    const {totalValue} = this.state;
    const data = props.payload[0];

    const value = Number(data.value);
    const percent = getPercentString(value, totalValue);
    const avgValue = data.payload.avgValue ? Number(data.payload.avgValue.toFixed(0)) : undefined;

    return (
      <div className="chart-tooltip">
        <div className="chart-title">
          {renderLegendDot(data.payload.fill)}
          <p>{percent} %</p>
        </div>
        <p>{data.name}</p>
        <p>{groupNumber(value)} {unit}</p>
        {avgValue && renderSmallText(`1 eszköz átlagosan ${groupNumber(avgValue)} ${unit}`)}
      </div>
    );
  };

  private renderLine = (props: PieLabelRenderProps) => {
    const startRadius = Number(props.outerRadius);
    const endRadius = startRadius + 20;

    const angle = Number(props.midAngle);
    const xD = Math.cos(-angle * RADIAN);
    const yD = Math.sin(-angle * RADIAN);

    const centerX = Number(props.cx);
    const centerY = Number(props.cy);
    const startX = centerX + startRadius * xD;
    const startY = centerY + startRadius * yD;
    const endX = centerX + endRadius * xD;
    const endY = centerY + endRadius * yD;

    const opacity = this.isVisible(props.percent!) ? 1.0 : 0.0;

    return (
      <path d={`M${startX},${startY}L${endX},${endY}`} stroke={props.stroke} opacity={opacity}/>
    );
  };


  private renderLabel = (props: PieLabelRenderProps) => {
    const angle = Number(props.midAngle);
    const radius = Number(props.outerRadius) + 30;

    const centerX = Number(props.cx);
    const centerY = Number(props.cy);
    const x = centerX + radius * Math.cos(-angle * RADIAN);
    const y = centerY + radius * Math.sin(-angle * RADIAN);

    const percent = props.percent! * 100;
    const opacity = this.isVisible(props.percent!) ? 0.65 : 0.0;

    return (
      <text
        x={x}
        y={y}
        opacity={opacity}
        textAnchor={x > centerX ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${percent.toFixed(1)}%`}
      </text>
    );
  };

  private isVisible = (percent: number): boolean => {
    const {data, totalValue} = this.state;
    if (totalValue === 0 || !data) {
      return false;
    }
    const percentOpacityThreshold = data.length > 30 ? 2 : 1;
    return percent * 100 > percentOpacityThreshold;
  };
}


// WEBPACK FOOTER //
// ./src/statistics/RentimeChart.tsx